import { createContext } from 'react'
import type { AuthState } from './authStore'
import type { AuthMode } from './ssoLogin'

export interface AuthApi {
  state: AuthState
  /** True while the app-level "Checking for an existing account…" notice
   *  should be showing -- see AuthProvider's afterSuccessfulAppleSignIn.
   *
   *  Optional, not required: several packages outside this one construct an
   *  `AuthApi` object literal for tests, and none of them exercise the Apple
   *  repair path. Making this required would force an unrelated edit onto
   *  every one of those call sites for a flag they can't produce. Real
   *  consumers always see a genuine boolean -- AuthProvider sets it via
   *  useState and never leaves it undefined. */
  checkingExistingAccount?: boolean
  /** Which login this deployment is configured for.
   *
   *  Optional for the same reason `checkingExistingAccount` is: dozens of
   *  packages build an `AuthApi` literal in their tests, and forcing a mode
   *  onto every one of them would buy nothing. Absent reads as `local`, which
   *  is what a hand-built literal is. */
  authMode?: AuthMode
  /** Hands the browser to natlink-sso to log in, returning to `returnTo`
   *  (a same-origin relative path) afterwards.
   *
   *  Present in sso mode only. Read it through `useSsoAuth()`, which turns a
   *  page mounted in the wrong mode into a loud error rather than a button
   *  that does nothing. */
  startSsoLogin?(returnTo?: string): Promise<void>
  /** The credential entry points below exist in LOCAL mode only. In sso mode
   *  the sso login page owns every one of them, so they are absent rather
   *  than present-and-throwing, and a page that needs them reads the api
   *  through `useLocalAuth()` -- which THROWS at mount in sso mode rather
   *  than handing back an api whose buttons would do nothing.
   *
   *  The compile-time half of that is narrower than "a password form in sso
   *  mode does not build": it covers a page that calls one of these off a
   *  bare `useAuth()`, where the optional member is a type error. A page that
   *  goes through `useLocalAuth()` compiles in both modes by design, and the
   *  mode mismatch surfaces as that mount-time throw. */
  signIn?(email: string, password: string): Promise<void>
  signInWithGoogle?(): Promise<void>
  /** Sign in with Apple (Firebase Apple OAuth provider — must be enabled in the
   *  Firebase console + Apple Developer for this to succeed). */
  signInWithApple?(): Promise<void>
  /** Create a new account with email + password. */
  signUp?(email: string, password: string): Promise<void>
  signOut(): Promise<void>
  sendPasswordReset?(email: string): Promise<void>
  /** Change the signed-in user's password.
   *
   *  The current password is re-verified against Firebase first, for two
   *  reasons: Firebase rejects `updatePassword` outright on a session that
   *  isn't recent, and the re-auth is what turns a wrong current password into
   *  a distinguishable `auth/wrong-password` instead of a generic failure —
   *  otherwise someone retypes a perfectly good new password because we never
   *  told them it was the OLD one we couldn't accept.
   *
   *  Rejects with the Firebase error (its `code` intact) so the caller can say
   *  which of those things went wrong. */
  changePassword(currentPassword: string, newPassword: string): Promise<void>
  applyActionCode(oobCode: string): Promise<void>
  /** Set auth persistence for the next sign-in: local (survives browser
   *  restarts, "remember me" on) vs session (cleared on tab close). */
  setRememberMe(remember: boolean): Promise<void>
}

/** The api as a local-mode page sees it: every credential entry point present. */
export type LocalAuthApi = AuthApi &
  Required<Pick<AuthApi, 'signIn' | 'signUp' | 'signInWithGoogle' | 'signInWithApple' | 'sendPasswordReset'>>

/** The api as an sso-mode page sees it. */
export type SsoAuthApi = AuthApi & Required<Pick<AuthApi, 'startSsoLogin'>>

export const AuthContext = createContext<AuthApi | null>(null)
